import { Component, AfterViewInit, ViewChild, ElementRef, Input, OnDestroy } from '@angular/core';
import { loadRemoteModule } from '@angular-architects/module-federation';
import { MF_MODULES } from '@app-core/constants/mf.constants';
import { environment } from '@env/environment';
import { TranslateService } from '@ngx-translate/core';
import { DataService } from '@app-core/services/data/data.service';
import { finalize, takeUntil, tap } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { API } from '@app-core/constants/api.constants';
import { AccessService } from '@app-core/services/access/access.service';
import { NavigationExtras, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { getDurationFilterState, getHomeTags, State } from 'src/app/home/reducers';
import { DurationRangeService } from '@app-core/services/duration-range/duration-range.service';
import { DateService } from '@app-core/services/date/date.service';
import { DurationRange } from '@app-core/models/core.model';
import { GoogleTagManagerService } from '@app-core/services/google-tag-manager/google-tag-manager.service';
import { MixPanelService } from '@app-core/services/mixpanel/mix-panel.service';
import { IncidentModalComponent } from '../incident-modal/incident-modal.component';
import { MatDialog } from '@angular/material/dialog';
import { HomeService } from '@app-home/services/home.service';
import { DownloadService } from '@app-shared/services/common/download.service';
import { SnackBarService } from '@app-core/services/snackbar/snack-bar.service';
import { HttpCacheService } from '@app-core/services/cache/cache.service';
import { getCurrentAccessDetailsState } from '@app-shared/reducers';
import { DownloadReportComponent } from '../download-report/download-report.component';
import { AsyncReportConfig, AsyncReportType } from '@app-core/constants/constants';
import { ApplicationRef, createComponent, EnvironmentInjector } from '@angular/core';
import { DurationRangeComponent } from '../duration-range/duration-range.component';
import { UpdateDurationFilter } from '@app-home/actions/duration-filter.actions';
import { TaggingService } from '@app-asset-config/services/tagging.service';
import { TaggingFilterV3Component } from '../tagging-filter-v3/tagging-filter-v3.component';
import { TaggingFilterV2Component } from '../tagging-filter-v2/tagging-filter-v2.component';
import { IncidentModalV2Component } from '../incident-modal-v2/incident-modal-v2.component';
import { getShadowRoot } from 'src/app/geotab/geotab-utils';
import { AsyncDownloadsService } from '@app-core/services/async-downloads/async-downloads.service';

@Component({
  selector: 'app-mf-loader',
  template: '<div #mfContainer></div>',
})
export class MFLoaderComponent implements AfterViewInit, OnDestroy {
  @ViewChild('mfContainer', { static: true }) mfContainer!: ElementRef;

  @Input()
  public name: string;

  @Input()
  public module: string;

  @Input()
  public remoteEntry: string;

  @Input()
  public props: any;

  @Input() onTagsChange?: (tags: any[]) => void;

  private theme: string;
  private currentLanguage: string = '';
  private currentDateFormat: string = '';
  private isReadableDateFormat: boolean;
  private isDriverCentric: boolean;
  private currentTimeZone: string = '';
  private id: string;
  private ngUnsubscribe: Subject<void> = new Subject<void>();
  public currentDurationFilterState: any;
  public startDate: string;
  public endDate: string;
  public currentMetricUnit = null;
  public durationList = this.durationService.getDurationList();
  public durationRange: DurationRange;
  public selectedTags: string[] = [];
  public currentFleet: string;
  public userPermissions = [];
  public userUIPermissions = [];
  public tags: any;
  private _mountedComponents = new Map<HTMLElement, any>();
  private __unmount: () => void | null = null;
  private __updateContext: (context: any) => void | null = null;
  private __updateProps: (props: any) => void | null = null;

  constructor(
    private dataService: DataService,
    private translate: TranslateService,
    private accessService: AccessService,
    private router: Router,
    private store: Store<State>,
    private durationService: DurationRangeService,
    private dateService: DateService,
    private gtmService: GoogleTagManagerService,
    private mixpanelService: MixPanelService,
    private dialog: MatDialog,
    private homeService: HomeService,
    private downloadService: DownloadService,
    private snackbarService: SnackBarService,
    private cacheService: HttpCacheService,
    private appRef: ApplicationRef,
    private injector: EnvironmentInjector,
    private taggingService: TaggingService,
    private asyncDownloadsService: AsyncDownloadsService
  ) {
    this.id = `mf-loader-${Math.random().toString(36).substring(2, 15)}`;

    this.dataService._currentLanguage.pipe(takeUntil(this.ngUnsubscribe)).subscribe((value: string) => {
      this.currentLanguage = value;
      // deliberately remounting the module on language change to ensure all data is refreshed
      this.remount();
    });

    this.dataService._currentDateFormat.pipe(takeUntil(this.ngUnsubscribe)).subscribe((value: string) => {
      this.currentDateFormat = value;
      this.updateContext({ dateFormat: this.currentDateFormat });
    });

    this.dataService._isReadableDateFormat.pipe(takeUntil(this.ngUnsubscribe)).subscribe((value: boolean) => {
      this.isReadableDateFormat = value;
      this.updateContext({ isReadableDateFormat: this.isReadableDateFormat });
    });

    this.dataService._isDriverCentric.pipe(takeUntil(this.ngUnsubscribe)).subscribe((value: boolean) => {
      this.isDriverCentric = value;
      this.remount();
    });

    this.dataService._currentTimeZone.pipe(takeUntil(this.ngUnsubscribe)).subscribe((value: string) => {
      this.currentTimeZone = value;
      this.updateContext({ timeZone: this.currentTimeZone });
    });

    this.store
      .select(getDurationFilterState)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((durationFilterState) => {
        const { startDate, endDate } = durationFilterState;
        this.currentDurationFilterState = durationFilterState;

        this.startDate = this.dateService.toDaysStartISO(startDate);
        this.endDate = this.dateService.toDaysEndISOPlusOne(endDate);

        this.updateContext({
          startDate: this.startDate,
          endDate: this.endDate,
          currentDurationFilterState: { ...durationFilterState },
        });
      });

    this.store
      .select(getHomeTags)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((getHomeTags) => {
        const { tagIds } = getHomeTags;
        this.selectedTags = tagIds;
        this.updateContext({ selectedTags: this.selectedTags });
      });

    window.addEventListener('tags:selected', this.tagListener);

    this.dataService._currentMetricUnit.subscribe((value: string) => {
      if (value) {
        this.currentMetricUnit = value;
        this.updateContext({ unit: this.currentMetricUnit });
      }
    });

    this.dataService._currentTheme.pipe(takeUntil(this.ngUnsubscribe)).subscribe((value: string) => {
      this.theme = value;
      this.updateContext({ themeMode: this.theme, theme: this.getPortalTheme() });
    });

    this.dataService._currentFleet.pipe(takeUntil(this.ngUnsubscribe)).subscribe((value: string) => {
      this.currentFleet = value;
      // deliberately remounting the module on fleet change to ensure all data is refreshed
      this.remount();
    });

    this.store
      .select(getCurrentAccessDetailsState)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(({ permissions = [], uiConfigurations = [] }) => {
        // trigger remount on any access change
        this.userPermissions = permissions;
        this.userUIPermissions = uiConfigurations;
        this.remount();
      });
  }

  public async mount() {
    if (!this.currentLanguage || !this.currentFleet) {
      console.warn(`[${this.id}] Cannot mount module "${this.module}" in "${this.name}" due to missing required information.`);
      return;
    }

    if (this.__unmount) {
      console.warn(
        `[${this.id}] A previous instance of the module "${this.module}" is still mounted. Unmounting it before mounting a new one.`
      );
      this.unmount();
    }

    console.log(`[${this.id}] MF Loader initialized, loading module "${this.module}" from "${this.name}" with props`, this.props);

    const { remoteName, remoteEntry } = this.getRemoteInfo();

    if (!remoteName || !remoteEntry) {
      console.error(`[${this.id}] Remote module information is incomplete. Cannot load module "${this.module}" in "${this.name}".`);
      return;
    }

    const exposedModule = this.getModuleName();

    if (!exposedModule) {
      console.error(`[${this.id}] Module name could not be determined for "${this.module}".`);
      return;
    }

    console.log(`[${this.id}] Loading remote module "${this.module}" from "${remoteName}" at "${remoteEntry}"`);

    const module = await loadRemoteModule({
      remoteEntry,
      remoteName,
      exposedModule,
    });

    console.log(`[${this.id}] Remote module "${this.module}" loaded successfully from "${remoteName}"`);

    const container = this.mfContainer.nativeElement;

    const render = module.bootstrap || module.render || module.default;
    const result = await render({
      id: this.id,
      container,
      context: this.getContext(),
      props: this.props || {},
    });

    if (result && typeof result === 'function') {
      this.__unmount = result;
    } else if (result && typeof result === 'object' && result.unmount && typeof result.unmount === 'function') {
      const { unmount, updateContext, updateProps } = result;
      this.__unmount = unmount;
      this.__updateProps = updateProps;
      this.__updateContext = updateContext;
    }

    this.injectStyleSheetIntoShadowDOM();
  }

  private injectStyleSheetIntoShadowDOM() {
    if (!environment.isGeotabEnv) return;

    const retries = 10;
    const delay = 500;
    let attempts = 0;

    const tryInject = () => {
      const link = document.querySelector('link[href*="assets.lightmetrics.co"][href$=".css"]') as HTMLLinkElement;
      if (link) {
        const shadowRoot = getShadowRoot();
        if (shadowRoot && !shadowRoot.querySelector(`link[href="${link.href}"]`)) {
          shadowRoot.appendChild(link.cloneNode(true));
        }
        return;
      }
      // Retry if not found
      if (attempts < retries) {
        attempts++;
        setTimeout(tryInject, delay);
        // } else {
        // console.warn('RideView MF stylesheets not found after retries.');
      }
    };

    tryInject();
  }

  public unmount() {
    if (this.__unmount) {
      console.log(`[${this.id}] Unmounting module "${this.module}" from "${this.name}"`);
      this.__unmount();

      this.__unmount = null;
    }
  }

  public updateContext(newContext: any) {
    if (this.__updateContext) {
      this.__updateContext(newContext);
    } else if (this.__unmount) {
      console.warn(`[${this.id}] Cannot update context for module "${this.module}" because it does not support context updates.`);
      this.mount(); // force remount to pass new context
    }
  }

  public updateProps(newProps: any) {
    if (this.__updateProps) {
      this.__updateProps(newProps);
    } else if (this.__unmount) {
      console.warn(`[${this.id}] Cannot update props for module "${this.module}" because it does not support prop updates.`);
      this.mount(); // force remount to pass new props
    }
  }

  public remount() {
    if (this.__unmount) {
      console.log(`[${this.id}] Remounting module "${this.module}" in "${this.name}"`);
      this.mount();
    }
  }

  public checkFeatureFlag(featueFlagPermissions: string[]) {
    const planDetails = this.durationService.planDetails;
    if (!planDetails) {
      return true;
    }
    const featureFlags = planDetails?.featureFlags;
    const showFeature = featueFlagPermissions.every((val: string) => featureFlags.includes(val));
    if (showFeature) {
      return true;
    } else {
      return false;
    }
  }

  public checkPermissions(permissions, showErrorMessage?) {
    showErrorMessage = showErrorMessage || false;
    const hasPermissions = permissions.every((val: string) => this.userPermissions.includes(val));
    if (hasPermissions) {
      return true;
    } else if (showErrorMessage) {
      return true;
    } else {
      return false;
    }
  }

  public checkUIPermissions(permissions, showErrorMessage?) {
    showErrorMessage = showErrorMessage || false;
    const hasPermissions = permissions.every((val: string) => this.userUIPermissions.includes(val));
    if (hasPermissions) {
      return true;
    } else if (showErrorMessage) {
      return true;
    } else {
      return false;
    }
  }

  public coachIncident(incidentDetails: any) {
    const assetId = incidentDetails?.asset?.assetId ?? incidentDetails?.assetId;

    this.gtmService.coachinginFleetIncidentList(incidentDetails.eventTypeLabel, assetId);
    this.mixpanelService.track('Click select for coaching - Coaching Tab', {
      'Event Type': incidentDetails.eventTypeLabel,
      'Asset ID': assetId,
    });
    this.updateIncident(incidentDetails, 'COACHING');
  }

  public discardIncident(incidentDetails: any) {
    const assetId = incidentDetails?.asset?.assetId ?? incidentDetails?.assetId;

    this.gtmService.discardIncidentInFleetIncidentList(incidentDetails.eventTypeLabel, assetId);
    this.mixpanelService.track('Click Discard event - Coaching Tab', {
      'Event Type': incidentDetails.eventTypeLabel,
      'Asset ID': assetId,
    });
    this.updateIncident(incidentDetails, 'REPORT_BUG');
  }

  public navigateToDriverDetailsPage(driverId: string, listType) {
    if (listType === 'topDrivers') {
      this.gtmService.gotoDriversPageFromFleetTopDriversTable(driverId);
    } else {
      this.gtmService.gotoDriversPageFromFleetRequireCoachingTable(driverId);
    }
    this.router.navigate(['/drivers/driver-details'], {
      queryParams: {
        driverId: driverId,
      },
    });
  }
  public viewTripDetails(incident: any) {
    const assetId = incident?.asset?.assetId ?? incident?.assetId;

    this.gtmService.gotoTripDetailsFromFleetIncidentsList(incident.eventTypeLabel, assetId);
    this.router.navigate(['/trip-details'], {
      queryParams: {
        tripId: incident.tripId,
        driverId: incident.driverId,
      },
    });
    this.mixpanelService.track('Click view trip details - Coaching Tab', {
      'Event Type': incident.eventTypeLabel,
      'Asset ID': incident.asset.assetId,
    });
  }

  private updateIncident(incidentDetails: any, updateType: string, eventBookmarked?: boolean) {
    incidentDetails.actionLoader = true;
    const { tripId = '', driverId = '', eventIndex = '' } = incidentDetails;
    const { loginName: userEmail, name: userName } = this.accessService.getLoginInfo();
    const params = {
      tripId,
      driverId,
      events: [
        {
          eventIndex,
          ...(incidentDetails.eventType === 'PotentialCrash' && {
            eventType: incidentDetails.eventType,
          }),
          ...(updateType === 'REPORT_BUG' && {
            reportBug: true,
            challengeRaised: true,
            challengeAccepted: true,
            challengeResolved: true,
          }),
          ...(updateType === 'COACHING' && {
            bookmark: true,
            coachingCompleted: false,
            coachingInitiatedMetadata: {
              userType: 'FLEET_MANAGER',
              email: userEmail,
              name: userName,
            },
          }),
          ...(eventBookmarked !== undefined && {
            eventBookmarked,
          }),
        },
      ],
    };
    this.dataService
      .updateEventMetadata(params)
      .pipe(
        tap(() => {
          this.cacheService.burstCache$.next(API.GET_SEVERE_VIOLATIONS);
        }),
        finalize(() => {
          incidentDetails.actionLoader = false;
        }),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe(
        () => {
          if (updateType === 'REPORT_BUG') {
            incidentDetails.reportBug = true;
            this.snackbarService.success(this.translate.instant('commonIncidentViewUpdatedSuccessfully'));
          } else if (updateType === 'VIEWED_STATUS') {
            incidentDetails.isViewed = true;
          } else if (updateType === 'BOOKMARKED') {
            this.snackbarService.success(this.translate.instant('Event Bookmarked.'));
          } else if (updateType === 'REMOVE_BOOKMARKED') {
            this.snackbarService.success(this.translate.instant('Bookmarked removed.'));
          } else {
            incidentDetails.bookmark = true;
            this.snackbarService.success(this.translate.instant('commonIncidentViewUpdatedSuccessfully'));
          }
        },
        () => {
          this.snackbarService.failure(this.translate.instant('commonIncidentViewUpdateFailed'));
        }
      );
  }

  public onDownloadVideos(request: any): void {
    this.downloadService.onDownloadVideos(request);
  }
  private updateEvent(tripId, events: any, updateType: string, showSnackbar) {
    events.forEach((element) => {
      element.actionLoader = true;
    });
    const { loginName: userEmail, name: userName } = this.accessService.getLoginInfo();
    const params = {
      tripId,
      driverId: events?.[0]?.driverId,
      events: events.map((e) => {
        return {
          eventIndex: e.eventIndex,
          ...(updateType === 'REPORT_BUG' && {
            reportBug: true,
            challengeRaised: true,
            challengeAccepted: true,
            challengeResolved: true,
          }),
          ...(updateType === 'COACHING' && {
            bookmark: true,
            coachingCompleted: false,
            coachingInitiatedMetadata: {
              userType: 'FLEET_MANAGER',
              email: userEmail,
              name: userName,
            },
          }),
          ...(updateType === 'VIEWED_STATUS' && { isViewed: true }),
        };
      }),
    };
    this.dataService
      .updateEventMetadata(params)
      .pipe(
        finalize(() => {
          events.forEach((element) => {
            element.actionLoader = false;
          });
        }),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe(
        () => {
          if (updateType === 'REPORT_BUG') {
            events.forEach((element) => {
              element.reportBug = true;
            });
          } else if (updateType === 'COACHING') {
            events.forEach((element) => {
              element.bookmark = true;
            });
          } else if (updateType === 'VIEWED_STATUS') {
            events.forEach((element) => {
              element.isViewed = true;
            });
          }
          if (showSnackbar) {
            this.snackbarService.success(`${this.translate.instant('commonIncidentViewUpdatedSuccessfully')}`);
          }
        },
        () => {
          if (showSnackbar) {
            this.snackbarService.failure(`${this.translate.instant('commonIncidentViewUpdateFailed')}`);
          }
        }
      );
  }
  public showEventModal(event: any = {}) {
    event.showSnackbar = false;
    this.mixpanelService.track('View Safety Events - Incident Video', {
      'Event Type Label': event.eventTypeLabel,
    });
    if (!event.isViewed) {
      this.updateEvent(event.tripId, [event], 'VIEWED_STATUS', event.index);
    }

    // modifying data for show only viewable pages
    if (this.durationService.enableNewIncidentModal) {
      this.dialog.open(IncidentModalV2Component, {
        panelClass: ['incident-modal-v2', 'mobile-modal'],
        position: { top: '24px', bottom: '24px' },
        autoFocus: false,
        disableClose: true,
        data: {
          source: 'IncidentView',
          allEvents: [event],
          currentIndex: 0,
          hidePagination: true,
        },
      });
    } else {
      this.dialog.open(IncidentModalComponent, {
        panelClass: ['incident-modal', 'mobile-modal'],
        position: { top: '24px', bottom: '24px' },
        autoFocus: false,
        disableClose: true,
        data: {
          source: 'IncidentView',
          allEvents: [event],
          currentIndex: 0,
          hidePagination: true,
        },
      });
    }
  }

  public navigateToDownloads() {
    this.asyncDownloadsService.navigateToExportHistory(AsyncReportType.driverBehaviourSummary.key);
    this.mixpanelService.track('Export Options Clicked - home page');
  }

  private showMedia(event: any = {}, videoList: any, pageSize: number, onClose?: () => void, currentPage?: number) {
    const filteredDataForPagination = this.dataService.modifyDataBasedOnPageSize(videoList, event.positionIndex, pageSize);
    const filteredDataPaginationIndex = this.dataService.modifyPageIndexBasedOnPageSize(event.positionIndex, pageSize);

    this.gtmService.viewFleetHighlightsVideo(event.eventTypeLabel);
    this.mixpanelService.track('View Fleet Recommended Event video', {
      'Event type Label': event.eventTypeLabel,
    });
    if (this.durationService.enableNewIncidentModal) {
      const dialogRef = this.dialog.open(IncidentModalV2Component, {
        panelClass: ['incident-modal-v2', 'mobile-modal'],
        position: { top: '24px', bottom: '24px' },
        autoFocus: false,
        disableClose: true,
        data: {
          source: 'Fleet',
          allEvents: filteredDataForPagination,
          currentIndex: filteredDataPaginationIndex,
          pageIndex: currentPage - 1 || 0,
          batchSize: pageSize,
        },
      });

      dialogRef.afterClosed().subscribe(() => {
        if (onClose) onClose();
      });
    } else {
      const dialogRef = this.dialog.open(IncidentModalComponent, {
        panelClass: ['incident-modal', 'mobile-modal'],
        position: { top: '24px', bottom: '24px' },
        autoFocus: false,
        disableClose: true,
        data: {
          source: 'Fleet',
          allEvents: filteredDataForPagination,
          currentIndex: filteredDataPaginationIndex,
          pageIndex: currentPage - 1 || 0,
          batchSize: pageSize,
        },
      });

      dialogRef.afterClosed().subscribe(() => {
        if (onClose) onClose();
      });
    }
  }

  private getRemoteInfo() {
    if (this.remoteEntry) {
      return {
        remoteName: this.name,
        remoteEntry: this.remoteEntry,
      };
    }

    const config = MF_MODULES[this.name];

    if (!config) {
      console.error(`[${this.id}] Module "${this.module}" is not defined in MF_MODULES.`);
      return {};
    }

    if (!config.entry || !config.entry[environment.env]) {
      console.error(`[${this.id}] No entry point defined for module "${this.module}" in environment "${environment.env}".`);
      return {};
    }

    return {
      remoteName: config.name,
      remoteEntry: config.entry[environment.env],
    };
  }

  private getModuleName() {
    if (this.remoteEntry) {
      return this.module;
    }

    const config = MF_MODULES[this.name];
    if (!config) {
      console.error(`[${this.id}] MF "${this.name}" is not defined in MF_MODULES.`);
      return null;
    }

    if (!config.name) {
      console.error(`[${this.id}] MF "${this.name}" does not have a name defined in MF_MODULES.`);
      return null;
    }

    if (!config.modules || !config.modules.includes(this.module)) {
      console.error(
        `[${this.id}] Module "${this.module}" is not included in the modules of "${config.name}". Available modules: ${config.modules.join(
          ', '
        )}`
      );
      return null;
    }

    return this.module;
  }

  private getContext() {
    const loginInfo = this.accessService.getLoginInfo();

    return {
      id: this.id,
      user: this.getUserForContext(),

      language: this.currentLanguage,
      unit: this.currentMetricUnit,

      startDate: this.startDate,
      endDate: this.endDate,
      selectedTags: this.selectedTags,

      themeMode: this.theme,
      theme: this.getPortalTheme(),
      dateFormat: this.currentDateFormat,
      timeZone: this.currentTimeZone,
      isReadableDateFormat: this.isReadableDateFormat,
      isDriverCentric: this.isDriverCentric,

      currentDurationFilterState: this.currentDurationFilterState,
      showMedia: (event, videoList, pageSize, onClose, currentPage) => this.showMedia(event, videoList, pageSize, onClose, currentPage),
      showEventModal: (event) => this.showEventModal(event),
      openReportDialog: (reportType: string, reportConfig?: AsyncReportConfig) => this.openReportDialog(reportType, reportConfig),
      getChildLabel: (x) => this.homeService.getChildLabel(x),
      updateIncident: (incident, updateType, eventBookmarked) => this.updateIncident(incident, updateType, eventBookmarked),
      discardIncident: (incident) => this.discardIncident(incident),
      coachIncident: (incident) => this.coachIncident(incident),
      viewTripDetails: (incident) => this.viewTripDetails(incident),
      navigateToDriverDetailsPage: (driverId, listType) => this.navigateToDriverDetailsPage(driverId, listType),
      onDownloadVideos: (incident) => this.onDownloadVideos(incident),
      openFaq: (value) => this.dataService?.openFaq(value),
      checkFeatureFlag: (featueFlagPermissions) => this.checkFeatureFlag(featueFlagPermissions),
      checkPermissions: (permissions) => this.checkPermissions(permissions),
      checkUIPermissions: (permissions) => this.checkUIPermissions(permissions),
      navigateToDownloads: () => this.navigateToDownloads(),
      getDateRangeInISO: (value) => this.dateService.getDateRangeInISO(value), // Past 6 months
      t: (key: string) => this.translate.instant(key),

      track: (event: string, properties?: Record<string, any>) => {
        this.mixpanelService.track(event, properties);
      },

      navigate: (paths: string[] | string, extras?: NavigationExtras) => {
        if (!paths || (Array.isArray(paths) && !paths.every((p) => typeof p === 'string'))) {
          console.error(`[${this.id}] Invalid navigation path`, paths);
          return;
        }

        this.router.navigate(Array.isArray(paths) ? paths : [paths], extras).catch((err) => {
          console.error(`[${this.id}] Navigation error:`, err);
        });
      },

      snackbarService: this.bindServiceMethods(this.snackbarService),
      dataService: this.bindServiceMethods(this.dataService),

      auth: {
        apiBaseURL: environment.intermediate_server_url,
        authToken: loginInfo.token,
        tokenType: 'JWT',

        fleetId: this.currentFleet,
        clientId: loginInfo.customerName,
        getURL: (path) => API.getUrl(path),

        expiresAt: loginInfo.tokenExpiresAt,
        refreshToken: () => {
          const { token, tokenExpiresAt: expiresAt } = this.accessService.getLoginInfo();
          return { token, expiresAt: expiresAt };
        },
      },

      hostMount: (componentName: string, mfElement: HTMLElement, props: any) => {
        return this.hostMount(componentName, mfElement, props);
      },
      hostUnmount: (mfElement: HTMLElement) => {
        this.hostUnmount(mfElement);
      },
    };
  }

  private bindServiceMethods(service: any): any {
    const boundService: any = {};
    for (const key of Object.getOwnPropertyNames(Object.getPrototypeOf(service))) {
      if (typeof service[key] === 'function' && key !== 'constructor') {
        boundService[key] = service[key].bind(service);
      }
    }
    return Object.assign(boundService, service);
  }

  private getUserForContext() {
    const userInfo = this.accessService.getLoginInfo();
    if (!userInfo) {
      console.warn(`[${this.id}] No user information found for context.`);
      return {};
    }

    return {
      userId: userInfo.userId || '',
      customerName: userInfo.customerName || '',
      loginName: userInfo.loginName || '',
      userType: userInfo.userType || '',
      permissions: [],
      name: userInfo.name || '',
      loginType: userInfo.loginType || '',
    };
  }

  getPortalTheme() {
    const prefix = 'lm';
    const variables: Record<string, string> = {};
    const root = environment.isGeotabEnv ? getShadowRoot() : document;
    Array.from(root.styleSheets)
      .filter((sheet) => {
        return environment.isGeotabEnv
          ? !sheet.href?.startsWith('https://assets.lightmetrics.co')
          : sheet.href === null || sheet.href.startsWith(window.location.origin);
      })
      .forEach((styleSheet) => {
        try {
          Array.from(styleSheet.cssRules || []).forEach((rule) => {
            if (rule instanceof CSSStyleRule) {
              if (rule.selectorText === ':root') {
                Array.from(rule.style).forEach((property) => {
                  if (property.startsWith(`--${prefix}`)) {
                    const value = rule.style.getPropertyValue(property).trim();

                    const brandVariable = property.replace(`--${prefix}-`, '--brand-');
                    variables[brandVariable] = value;
                  }
                });
              }
            }
          });
        } catch (e) {
          console.warn('Could not access rules of stylesheet:', styleSheet.href, e);
        }
      });

    console.log('Portal theme variables:', variables);
    return variables;
  }

  private openDialog(component: any, reportType: string, reportConfig?: AsyncReportConfig): void {
    this.dialog.open(component, {
      width: '600px',
      minHeight: '300px',
      data: { reportType, currentReportConfig: reportConfig },
    });
  }

  private openReportDialog(reportType: string, reportConfig?: AsyncReportConfig): void {
    this.openDialog(DownloadReportComponent, reportType, reportConfig);
  }

  async ngAfterViewInit() {
    this.mount();
    console.log(`[${this.id}] MF Loader component initialized for module "${this.module}" in "${this.name}"`);
  }

  ngOnDestroy(): void {
    this.unmount();
    console.log(`[${this.id}] MF Loader component destroyed for module "${this.module}" in "${this.name}"`);

    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }
  hostMount(componentName: string, mfElement: HTMLElement, props: any) {
    const componentMap = this.hostAvailableComponents();
    const componentConfig = componentMap[componentName];
    const component = componentConfig?.components;
    const dependencies = componentConfig?.dependencies || {};

    if (!component) {
      console.error(`Component "${componentName}" not found`);
      return null;
    }

    const componentRef = createComponent(component, {
      environmentInjector: this.injector,
      hostElement: mfElement,
    });

    // This is the key step: assign all dependencies
    Object.entries(dependencies).forEach(([key, value]) => {
      componentRef.instance[key] = value;
    });

    const subscriptions: any[] = [];

    // Set inputs and subscribe to outputs
    if (props) {
      Object.entries(props).forEach(([key, value]) => {
        if (typeof value === 'function') {
          const eventEmitter = componentRef.instance[key];
          if (eventEmitter && eventEmitter.subscribe) {
            const sub = eventEmitter.subscribe((event: any) => value(event));
            subscriptions.push(sub);
          }
        } else {
          componentRef.setInput(key, value);
        }
      });
    }

    this.appRef.attachView(componentRef.hostView);

    componentRef.changeDetectorRef.detectChanges();

    this._mountedComponents.set(mfElement, componentRef);

    return {
      dependencies,
      update: (newProps: Record<string, any>) => {
        Object.entries(newProps).forEach(([key, value]) => {
          if (typeof value !== 'function') {
            componentRef.setInput(key, value);
          }
        });
        componentRef.changeDetectorRef.detectChanges();
      },

      destroy: () => {
        subscriptions.forEach((sub) => sub.unsubscribe());
        this.hostUnmount(mfElement);
      },
    };
  }

  hostUnmount(mfElement: HTMLElement) {
    const componentRef = this._mountedComponents.get(mfElement);
    if (componentRef) {
      this.appRef.detachView(componentRef.hostView);
      componentRef.destroy();
      this._mountedComponents.delete(mfElement);
    }
  }
  private tagListener = (event: any) => {
    // event.detail contains the selected tags from TagsComponent
    this.tags = event.detail;
    console.log(this.tags, 'Selected tags received from TagsComponent');

    if (this.onTagsChange) {
      this.onTagsChange(this.tags); // ✅ directly send tags to React
    }
    // Optional: map again if you only want IDs
    // const tagIds = this.selectedTags.map(t => t.tagId);
  };
  hostAvailableComponents() {
    const componentMap = {
      'app-duration-range': {
        components: DurationRangeComponent,
        dependencies: {
          durationService: this.durationService,
          updateDurationFilter: (params: { days: number; startDate: Date; endDate: Date }) => {
            // Dispatch to NgRx store
            this.store.dispatch(
              new UpdateDurationFilter({
                days: params.days,
                startDate: params.startDate,
                endDate: params.endDate,
              })
            );
          },
        },
      },

      'app-tagging-filter-v2': {
        components: TaggingFilterV2Component,
        dependencies: {
          taggingService: this.taggingService,
          isReactMicrofrontend: true,
          onTagsChange: (tags: any[]) => {
            console.log('Forwarding tags to host dependencies', tags);
          },
        },
      },

      'app-tagging-filter-v3': {
        components: TaggingFilterV3Component,
        dependencies: {
          taggingService: this.taggingService,
          isReactMicrofrontend: false,
          isIncidentModal: true,
          onTagsChange: (tags: any[]) => {
            console.log('Forwarding tags to host dependencies', tags);
          },
        },
      },
    };

    return componentMap;
  }
}
